class EmersonPdpDescription extends HTMLElement {
  connectedCallback() {
    this.inner = this.querySelector('.sc-description-inner');
    this.fade = this.querySelector('.sc-description-fade');
    this.toggle = this.querySelector('.sc-description-toggle');
    this.showMoreLabel = this.dataset.showMore || 'Show more details';
    this.showLessLabel = this.dataset.showLess || 'Show less';

    if (!this.inner || !this.toggle) return;

    this.collapsedHeight =
      parseInt(
        getComputedStyle(this).getPropertyValue('--sc-description-collapsed-height'),
        10
      ) || 200;

    this.onToggleClick = () => this.toggleExpand();
    this.toggle.addEventListener('click', this.onToggleClick);

    if (document.readyState === 'complete') {
      this.init();
    } else {
      window.addEventListener('load', () => this.init(), { once: true });
    }
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onToggleClick);
  }

  init() {
    const fullHeight = this.inner.scrollHeight;

    if (fullHeight <= this.collapsedHeight + 16) {
      this.classList.add('is-expanded');
      return;
    }

    this.classList.add('is-collapsible', 'is-collapsed');
    this.inner.style.maxHeight = `${this.collapsedHeight}px`;
    this.toggle.hidden = false;
    this.fade.hidden = false;
  }

  toggleExpand() {
    if (this.classList.contains('is-expanded')) {
      this.collapse();
    } else {
      this.expand();
    }
  }

  expand() {
    const targetHeight = this.inner.scrollHeight;

    this.classList.remove('is-collapsed');
    this.classList.add('is-expanded');
    this.inner.style.maxHeight = `${targetHeight}px`;
    this.toggle.textContent = this.showLessLabel;
    this.toggle.setAttribute('aria-expanded', 'true');

    const onEnd = (event) => {
      if (event.propertyName !== 'max-height') return;
      this.inner.removeEventListener('transitionend', onEnd);
      if (this.classList.contains('is-expanded')) {
        this.inner.style.maxHeight = 'none';
      }
    };

    this.inner.addEventListener('transitionend', onEnd);
  }

  collapse() {
    const currentHeight = this.inner.scrollHeight;
    this.inner.style.maxHeight = `${currentHeight}px`;

    requestAnimationFrame(() => {
      this.classList.remove('is-expanded');
      this.classList.add('is-collapsed');
      this.inner.style.maxHeight = `${this.collapsedHeight}px`;
    });

    this.toggle.textContent = this.showMoreLabel;
    this.toggle.setAttribute('aria-expanded', 'false');
  }
}

const PDP_MOBILE_MQ = window.matchMedia('(max-width: 900px)');

class EmersonPdpGallery extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = 0;
    this.slideData = [];
    this.inlineSwiper = null;
    this.lightbox = null;
    this.isSyncing = false;
    this.onThumbClick = this.onThumbClick.bind(this);
    this.onZoomClick = this.onZoomClick.bind(this);
    this.onInlineSlideChange = this.onInlineSlideChange.bind(this);
    this.onMediaQueryChange = this.onMediaQueryChange.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.resizeTimer = null;
  }

  connectedCallback() {
    if (this.dataset.galleryBound === 'true') return;
    this.dataset.galleryBound = 'true';

    this.slideData = [];
    this.buildSlideData();
    this.thumbs = Array.from(this.querySelectorAll('[data-pdp-thumb]'));
    if (!this.slideData.length && !this.thumbs.length) return;

    this.activeIndex = parseInt(this.dataset.initialSlide, 10) || 0;
    this.enableLightbox = this.dataset.enableLightbox === 'true';
    this.sectionId = this.dataset.sectionId;

    this.thumbs.forEach((thumb) => {
      if (!thumb.hidden) {
        thumb.addEventListener('click', this.onThumbClick);
      }
    });

    this.querySelectorAll('[data-pdp-zoom]').forEach((trigger) => {
      trigger.addEventListener('click', this.onZoomClick);
    });

    this.resolveLightbox();
    queueMicrotask(() => this.resolveLightbox());

    this.initInlineSwiper();
    this.syncInlineSwiperHeight();
    PDP_MOBILE_MQ.addEventListener('change', this.onMediaQueryChange);
    window.addEventListener('resize', this.onResize);
    this.goToSlide(this.activeIndex, { syncInline: false, syncThumbs: true, syncDesktop: true });
  }

  disconnectedCallback() {
    this.thumbs?.forEach((thumb) => thumb.removeEventListener('click', this.onThumbClick));
    this.querySelectorAll('[data-pdp-zoom]').forEach((trigger) => {
      trigger.removeEventListener('click', this.onZoomClick);
    });
    PDP_MOBILE_MQ.removeEventListener('change', this.onMediaQueryChange);
    window.removeEventListener('resize', this.onResize);
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.resetInlineSwiperHeight();
    this.destroyInlineSwiper();
    if (this.lightbox?.gallery === this) {
      this.lightbox.gallery = null;
    }
  }

  buildSlideData() {
    this.slideData = [];
    const template = this.querySelector('[data-pdp-lightbox-template]');

    if (template?.content) {
      template.content.querySelectorAll('.swiper-slide').forEach((slide) => {
        const img = slide.querySelector('img');
        const index = parseInt(slide.dataset.pdpSlideIndex, 10);
        if (Number.isNaN(index)) return;

        const mediaType = slide.dataset.mediaType || 'image';
        const isVideo = mediaType === 'video' || mediaType === 'external_video';

        this.slideData.push({
          index,
          mediaId: slide.dataset.mediaId || null,
          mediaType,
          isVideo,
          src: img ? (img.getAttribute('src') || img.src) : null,
          alt: img ? (img.getAttribute('alt') || '') : '',
        });
      });
    }

    if (!this.slideData.length) {
      this.querySelectorAll('[data-pdp-thumb]').forEach((thumb) => {
        const index = parseInt(thumb.dataset.pdpSlideIndex, 10);
        if (Number.isNaN(index)) return;
        const mediaType = thumb.dataset.mediaType || 'image';
        this.slideData.push({
          index,
          mediaId: thumb.dataset.mediaId || null,
          mediaType,
          isVideo: mediaType === 'video' || mediaType === 'external_video',
          src: thumb.dataset.fullSrc,
          alt: thumb.dataset.alt || '',
        });
      });
    }

    this.slideData.sort((a, b) => a.index - b.index);
  }

  resolveLightbox() {
    if (!this.sectionId) return null;

    const lightbox =
      this.lightbox ||
      document.getElementById(`EmersonPdpLightbox-${this.sectionId}`);

    if (lightbox) {
      this.lightbox = lightbox;
      lightbox.gallery = this;
    }

    return this.lightbox;
  }

  pauseVideosIn(container) {
    if (!container) return;
    container.querySelectorAll('video').forEach((video) => {
      video.pause();
    });
    container.querySelectorAll('iframe').forEach((iframe) => {
      iframe.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
        '*'
      );
    });
  }

  syncDesktopSlide(index) {
    const desktopSlides = this.querySelectorAll('.sc-pdp-desktop-slide');
    if (!desktopSlides.length) return;

    desktopSlides.forEach((slide) => {
      const slideIndex = parseInt(slide.dataset.pdpSlideIndex, 10);
      const isActive = slideIndex === index;
      slide.hidden = !isActive;
      slide.classList.toggle('is-active', isActive);
      if (!isActive) {
        this.pauseVideosIn(slide);
      }
    });
  }

  syncInlineVideos(activeIndex) {
    const swiperEl = this.querySelector('[data-pdp-inline-swiper]');
    if (!swiperEl) return;

    swiperEl.querySelectorAll('.swiper-slide').forEach((slide, i) => {
      if (i !== activeIndex) {
        this.pauseVideosIn(slide);
      }
    });
  }

  getSlideCount() {
    const fromDataset = parseInt(this.dataset.slideCount, 10);
    if (fromDataset > 0) return fromDataset;
    return this.slideData.length || this.thumbs.length;
  }

  getSlideData(index) {
    return this.slideData.find((slide) => slide.index === index) || null;
  }

  getThumbByIndex(index) {
    return this.thumbs.find(
      (thumb) => parseInt(thumb.dataset.pdpSlideIndex, 10) === index
    );
  }

  findSlideIndexByMediaId(mediaId) {
    if (!mediaId) return -1;
    const slide = this.slideData.find(
      (s) => s.mediaId && String(s.mediaId) === String(mediaId)
    );
    if (slide) return slide.index;
    const thumb = this.thumbs.find(
      (t) => String(t.dataset.mediaId) === String(mediaId)
    );
    return thumb ? parseInt(thumb.dataset.pdpSlideIndex, 10) : -1;
  }

  findSlideIndexBySrc(src) {
    if (!src) return -1;
    const normalized = src.split('?')[0];
    const slide = this.slideData.find((s) => {
      const slideSrc = (s.src || '').split('?')[0];
      return slideSrc === normalized || normalized.includes(slideSrc) || slideSrc.includes(normalized);
    });
    if (slide) return slide.index;
    const thumb = this.thumbs.find((t) => {
      const thumbSrc = (t.dataset.fullSrc || '').split('?')[0];
      return thumbSrc === normalized || normalized.includes(thumbSrc);
    });
    return thumb ? parseInt(thumb.dataset.pdpSlideIndex, 10) : -1;
  }

  onThumbClick(event) {
    event.preventDefault();
    const index = parseInt(event.currentTarget.dataset.pdpSlideIndex, 10);
    if (Number.isNaN(index)) return;
    this.goToSlide(index);
  }

  onZoomClick(event) {
    event.preventDefault();
    event.stopPropagation();
    const lightbox = this.resolveLightbox();
    if (!this.enableLightbox || !lightbox) return;

    let index = this.activeIndex;
    const slideEl = event.currentTarget.closest('[data-pdp-slide-index]');
    if (slideEl) {
      index = parseInt(slideEl.dataset.pdpSlideIndex, 10);
    }
    if (Number.isNaN(index)) index = this.activeIndex;

    this.goToSlide(index);
    lightbox.open(index, event.currentTarget);
  }

  onKeyDown(event) {
    if (!this.enableLightbox || !this.lightbox?.hasAttribute('open')) return;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.lightbox.slidePrev();
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.lightbox.slideNext();
    }
  }

  getInlineSwiperEl() {
    return this.querySelector('[data-pdp-inline-swiper]');
  }

  measureSlideHeight(slide, containerWidth) {
    let height = 0;
    const img = slide.querySelector('img.sc-pdp-main-image, .deferred-media__poster img');

    if (img?.naturalWidth > 0 && img.naturalHeight > 0) {
      height = (containerWidth / img.naturalWidth) * img.naturalHeight;
    } else if (img?.height > 0 && img?.width > 0) {
      height = (containerWidth / img.width) * img.height;
    }

    const deferred = slide.querySelector('.sc-pdp-deferred-media');
    if (deferred) {
      const paddingBottom = deferred.style.paddingBottom || '';
      const match = paddingBottom.match(/([\d.]+)%/);
      if (match) {
        height = Math.max(height, containerWidth * (parseFloat(match[1]) / 100));
      }
    }

    return height;
  }

  async waitForInlineSwiperImages(el) {
    const images = Array.from(el.querySelectorAll('img'));
    await Promise.all(
      images.map(async (img) => {
        if (!img.complete) {
          img.loading = 'eager';
        }
        if (typeof img.decode === 'function') {
          try {
            await img.decode();
          } catch (_) {
            /* ignore decode errors */
          }
        }
        if (!img.complete) {
          await new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          });
        }
      })
    );
  }

  resetInlineSwiperHeight() {
    const el = this.getInlineSwiperEl();
    if (!el) return;

    el.classList.remove('sc-pdp-main-swiper--uniform-height');
    el.style.removeProperty('--sc-pdp-swiper-height');
    el.style.height = '';
    el.style.maxHeight = '';
  }

  async syncInlineSwiperHeight() {
    if (!PDP_MOBILE_MQ.matches) {
      this.resetInlineSwiperHeight();
      return;
    }

    const el = this.getInlineSwiperEl();
    if (!el) return;

    await this.waitForInlineSwiperImages(el);

    const containerWidth = el.clientWidth || el.offsetWidth;
    if (!containerWidth) return;

    const slides = el.querySelectorAll('.swiper-slide');
    if (!slides.length) return;

    let maxHeight = 0;
    slides.forEach((slide) => {
      maxHeight = Math.max(maxHeight, this.measureSlideHeight(slide, containerWidth));
    });

    if (maxHeight <= 0) return;

    const titleEl = document.querySelector('.sc-pdp-title--above-gallery');
    const titleBottom = titleEl ? titleEl.getBoundingClientRect().bottom : 0;
    const viewportCap = Math.floor(window.innerHeight - titleBottom - 250);
    const cappedHeight = Math.min(maxHeight, Math.max(200, viewportCap));
    const heightPx = `${Math.ceil(cappedHeight)}px`;
    el.classList.add('sc-pdp-main-swiper--uniform-height');
    el.style.setProperty('--sc-pdp-swiper-height', heightPx);
    el.style.height = heightPx;
    el.style.maxHeight = heightPx;

    if (this.inlineSwiper) {
      this.inlineSwiper.update();
    }
  }

  onResize = () => {
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.syncInlineSwiperHeight();
    }, 150);
  };

  initInlineSwiper() {
    if (!PDP_MOBILE_MQ.matches || typeof Swiper === 'undefined') return;

    const el = this.querySelector('[data-pdp-inline-swiper]');
    if (!el || el.querySelectorAll('.swiper-slide').length <= 1) return;

    this.destroyInlineSwiper();

    const pagination = el.querySelector('.sc-pdp-main-swiper__pagination');
    this.inlineSwiper = new Swiper(el, {
      slidesPerView: 1,
      spaceBetween: 0,
      initialSlide: this.activeIndex,
      touchStartPreventDefault: false,
      pagination: pagination
        ? { el: pagination, clickable: true }
        : undefined,
      on: {
        slideChange: this.onInlineSlideChange,
      },
    });

    this.syncInlineSwiperHeight();
  }

  destroyInlineSwiper() {
    if (this.inlineSwiper) {
      this.inlineSwiper.destroy(true, true);
      this.inlineSwiper = null;
    }
  }

  onMediaQueryChange() {
    if (PDP_MOBILE_MQ.matches) {
      this.initInlineSwiper();
      if (this.inlineSwiper) {
        this.inlineSwiper.slideTo(this.activeIndex, 0);
      } else {
        this.syncInlineSwiperHeight();
      }
    } else {
      this.resetInlineSwiperHeight();
      this.destroyInlineSwiper();
    }
  }

  onInlineSlideChange() {
    if (this.isSyncing || !this.inlineSwiper) return;
    this.goToSlide(this.inlineSwiper.activeIndex, {
      syncInline: false,
      syncThumbs: true,
      syncDesktop: true,
    });
  }

  goToSlide(index, options = {}) {
    const {
      syncInline = true,
      syncThumbs = true,
      syncDesktop = true,
      syncLightbox = true,
    } = options;

    const count = this.getSlideCount();
    if (!count) return;

    const nextIndex = Math.max(0, Math.min(index, count - 1));
    this.activeIndex = nextIndex;

    if (syncThumbs) {
      this.thumbs.forEach((thumb) => {
        const thumbIndex = parseInt(thumb.dataset.pdpSlideIndex, 10);
        thumb.classList.toggle('active', thumbIndex === nextIndex);
      });
    }

    if (syncDesktop) {
      this.syncDesktopSlide(nextIndex);
    }

    if (syncInline && this.inlineSwiper && this.inlineSwiper.activeIndex !== nextIndex) {
      this.isSyncing = true;
      this.inlineSwiper.slideTo(nextIndex);
      this.isSyncing = false;
    }

    this.syncInlineVideos(nextIndex);

    const lightbox = syncLightbox ? this.resolveLightbox() : null;
    if (lightbox?.swiper && lightbox.hasAttribute('open')) {
      if (lightbox.swiper.activeIndex !== nextIndex) {
        lightbox.swiper.slideTo(nextIndex);
      }
      lightbox.updateCounter();
    }
  }
}

class EmersonPdpLightbox extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.gallery = null;
    this.lastOpener = null;
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onBackdropClick = this.onBackdropClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
  }

  connectedCallback() {
    if (this.dataset.lightboxBound === 'true') return;
    this.dataset.lightboxBound = 'true';

    if (!this.moved) {
      this.moved = true;
      document.body.appendChild(this);
    }

    this.dialog = this.querySelector('.sc-pdp-lightbox__dialog');
    this.wrapper = this.querySelector('[data-pdp-lightbox-wrapper]');
    this.counter = this.querySelector('[data-pdp-lightbox-counter]');
    this.closeBtn = this.querySelector('[data-pdp-lightbox-close]');
    this.prevBtn = this.querySelector('[data-pdp-lightbox-prev]');
    this.nextBtn = this.querySelector('[data-pdp-lightbox-next]');
    this.swiperEl = this.querySelector('[data-pdp-lightbox-swiper]');

    this.closeBtn?.addEventListener('click', this.onCloseClick);
    this.addEventListener('click', this.onBackdropClick);
    this.addEventListener('keyup', this.onKeyUp);

    this.bindGallery();
  }

  bindGallery() {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const gallery = document.querySelector(
      `emerson-pdp-gallery[data-section-id="${sectionId}"]`
    );
    if (!gallery) return;

    this.gallery = gallery;
    gallery.lightbox = this;
  }

  disconnectedCallback() {
    this.closeBtn?.removeEventListener('click', this.onCloseClick);
    this.removeEventListener('click', this.onBackdropClick);
    this.removeEventListener('keyup', this.onKeyUp);
    this.destroySwiper();
  }

  getSlideCount() {
    return parseInt(this.dataset.slideCount, 10) || 0;
  }

  ensureSwiper() {
    if (this.swiper || typeof Swiper === 'undefined') return;

    if (!this.gallery) {
      this.bindGallery();
    }

    const gallery = this.gallery;
    const template = gallery?.querySelector('[data-pdp-lightbox-template]');
    if (!template?.content || !this.wrapper) return;

    this.wrapper.replaceChildren(template.content.cloneNode(true));

    const slideCount = this.wrapper.querySelectorAll('.swiper-slide').length;
    const showNav = slideCount > 1;

    if (this.prevBtn) this.prevBtn.hidden = !showNav;
    if (this.nextBtn) this.nextBtn.hidden = !showNav;

    this.swiper = new Swiper(this.swiperEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      navigation: showNav
        ? {
            nextEl: this.nextBtn,
            prevEl: this.prevBtn,
          }
        : undefined,
      keyboard: { enabled: true, onlyInViewport: true },
      on: {
        slideChange: () => {
          if (!this.gallery || this.gallery.isSyncing) return;
          this.gallery.goToSlide(this.swiper.activeIndex, {
            syncInline: true,
            syncThumbs: true,
            syncDesktop: true,
            syncLightbox: false,
          });
          this.updateCounter();
        },
      },
    });
  }

  destroySwiper() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  open(index = 0, opener = null) {
    this.lastOpener = opener;
    this.ensureSwiper();
    if (!this.swiper) return;

    this.removeAttribute('hidden');
    this.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');

    this.swiper.slideTo(index, 0);
    this.updateCounter();

    if (typeof trapFocus === 'function' && this.dialog) {
      trapFocus(this.dialog, this.closeBtn);
    }
  }

  close() {
    this.removeAttribute('open');
    this.setAttribute('hidden', '');

    const stillOpen = document.querySelector('emerson-pdp-lightbox[open], .product-media-modal[open], details[open].menu-drawer-container');
    if (!stillOpen) {
      document.body.classList.remove('overflow-hidden');
    }

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.lastOpener);
    }
  }

  slidePrev() {
    this.swiper?.slidePrev();
  }

  slideNext() {
    this.swiper?.slideNext();
  }

  updateCounter() {
    if (!this.counter || !this.swiper) return;
    const total = this.getSlideCount() || this.swiper.slides.length;
    if (total <= 1) {
      this.counter.textContent = '';
      return;
    }
    this.counter.textContent = `${this.swiper.activeIndex + 1} / ${total}`;
  }

  onCloseClick(event) {
    event.preventDefault();
    this.close();
  }

  onBackdropClick(event) {
    if (
      event.target.closest(
        '.sc-pdp-lightbox__image, .sc-pdp-lightbox__close, .sc-pdp-lightbox__nav'
      )
    ) {
      return;
    }

    this.close();
  }

  onKeyUp(event) {
    if (event.code?.toUpperCase() === 'ESCAPE') {
      this.close();
    }
  }
}

class EmersonPdpQuantity extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-qty-input]');
    if (!this.input) return;
    this.querySelectorAll('[data-qty-change]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const next = Math.max(1, Number(this.input.value) + Number(btn.dataset.qtyChange));
        this.input.value = next;
      });
    });
  }
}

class EmersonPdpStickyBar extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.submitId = this.dataset.submitId;
    this.priceId = this.dataset.priceId;
    this.stickyAtc = this.querySelector('[data-sticky-atc]');
    this.stickyPrice = this.querySelector('[data-sticky-price]');
    this.mainSubmit = document.getElementById(this.submitId);
    this.priceEl = document.getElementById(this.priceId);

    if (!this.mainSubmit || !this.stickyAtc) return;

    this.syncPrice();
    this.syncSubmitState();

    this.stickyAtc.addEventListener('click', () => {
      if (!this.mainSubmit.disabled) {
        this.mainSubmit.click();
      }
    });

    this.observer = new IntersectionObserver(
      (entries) => {
        const visible = entries[0]?.isIntersecting;
        if (visible) {
          this.classList.remove('is-visible');
          this.setAttribute('hidden', '');
        } else {
          this.removeAttribute('hidden');
          this.classList.add('is-visible');
        }
      },
      { root: null, threshold: 0, rootMargin: '0px' }
    );
    this.observer.observe(this.mainSubmit);

    if (this.priceEl) {
      this.priceObserver = new MutationObserver(() => this.syncPrice());
      this.priceObserver.observe(this.priceEl, { childList: true, subtree: true, characterData: true });
    }

    document.addEventListener('variant:change', this.onVariantChange);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.priceObserver?.disconnect();
    document.removeEventListener('variant:change', this.onVariantChange);
  }

  onVariantChange = (event) => {
    const { sectionId, variant } = event.detail || {};
    if (sectionId && String(sectionId) !== String(this.sectionId)) return;
    this.syncPrice();
    if (variant !== undefined) {
      this.stickyAtc.disabled = !variant?.available;
    } else {
      this.syncSubmitState();
    }
  };

  syncPrice() {
    if (!this.stickyPrice || !this.priceEl) return;
    this.stickyPrice.innerHTML = this.priceEl.innerHTML;
  }

  syncSubmitState() {
    if (!this.stickyAtc || !this.mainSubmit) return;
    this.stickyAtc.disabled = this.mainSubmit.disabled;
    this.stickyAtc.textContent = this.mainSubmit.textContent.trim();
  }
}

customElements.define('emerson-pdp-description', EmersonPdpDescription);
customElements.define('emerson-pdp-gallery', EmersonPdpGallery);
customElements.define('emerson-pdp-lightbox', EmersonPdpLightbox);
customElements.define('emerson-pdp-quantity', EmersonPdpQuantity);
customElements.define('emerson-pdp-sticky-bar', EmersonPdpStickyBar);

function updateEmersonSelectedColor(sectionId) {
  if (!sectionId) return;

  const section = document.getElementById(`shopify-section-${sectionId}`);
  if (!section) return;

  const colorFieldset = section.querySelector('.sc-pdp-color-picker .js-radio-colors');
  if (!colorFieldset) return;

  const selectedInput = colorFieldset.querySelector('input:checked');
  const valueEl = section.querySelector('[data-selected-color]');
  if (!selectedInput || !valueEl) return;

  const swatchItem = selectedInput.closest('.sc-pdp-color-swatch-item');
  const statusEl = swatchItem?.querySelector('.sc-pdp-color-swatch-item__status');
  valueEl.textContent = statusEl
    ? `${selectedInput.value} (${statusEl.textContent.trim()})`
    : selectedInput.value;
}

function handleEmersonVariantGalleryChange(variant, sectionId) {
  if (!sectionId) return;

  updateEmersonSelectedColor(sectionId);

  const gallery = document.querySelector(
    `emerson-pdp-gallery[data-section-id="${sectionId}"]`
  );
  if (!gallery) return;

  let slideIndex = -1;
  if (variant?.featured_media?.id) {
    slideIndex = gallery.findSlideIndexByMediaId(variant.featured_media.id);
  }
  if (slideIndex < 0 && variant?.featured_image?.src) {
    slideIndex = gallery.findSlideIndexBySrc(variant.featured_image.src);
  }

  if (slideIndex >= 0) {
    gallery.goToSlide(slideIndex);
  }
}

document.addEventListener('variant:change', (event) => {
  const { variant, sectionId } = event.detail || {};
  handleEmersonVariantGalleryChange(variant, sectionId);
});

document.addEventListener('change', (event) => {
  const colorFieldset = event.target.closest('.sc-pdp-color-picker .js-radio-colors');
  if (!colorFieldset) return;

  const section = colorFieldset.closest('[data-section-id]');
  if (section) {
    updateEmersonSelectedColor(section.dataset.sectionId);
  }
});

document.querySelectorAll('.sc-pdp-section[data-section-id]').forEach((section) => {
  updateEmersonSelectedColor(section.dataset.sectionId);
});

document.addEventListener('shopify:section:load', (event) => {
  const section = event.target.querySelector?.('.sc-pdp-section[data-section-id]');
  if (section) {
    updateEmersonSelectedColor(section.dataset.sectionId);
  }
});
